import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CTA_ORANGE, RADIUS } from './dashboardColors';
import { pressableSx } from './riegoGantt.styles';
import type {
  ScheduleIrrigationModalProps, StartFormType, StartIrrigationInput,
} from './riego.types';
import {
  DEFAULT_DURATION_MIN,
  DEFAULT_PRECIPITATION_MM,
  END_TIME_LEAD_MIN,
  INIT_TIME_LEAD_MIN,
  MIN_DURATION_MIN,
  MS_PER_MINUTE,
  ORPHAN_GROUP,
  START_TYPE_LABEL,
  START_TYPE_ORDER,
} from './riego.constants';

const toLocalInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const leadFrom = (minutes: number): string => (
  toLocalInput(new Date(Date.now() + minutes * MS_PER_MINUTE))
);

const ScheduleIrrigationModal: React.FC<ScheduleIrrigationModalProps> = ({
  open, onClose, pumpSystems, zones, isSending, onSubmit,
}) => {
  const [zoneId, setZoneId] = useState<number | ''>('');
  const [type, setType] = useState<StartFormType>('duration');
  const [durationMin, setDurationMin] = useState(String(DEFAULT_DURATION_MIN));
  const [precipitation, setPrecipitation] = useState(String(DEFAULT_PRECIPITATION_MM));
  const [initTime, setInitTime] = useState(() => leadFrom(INIT_TIME_LEAD_MIN));
  const [endTime, setEndTime] = useState(() => leadFrom(END_TIME_LEAD_MIN));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setZoneId('');
    setType('duration');
    setDurationMin(String(DEFAULT_DURATION_MIN));
    setPrecipitation(String(DEFAULT_PRECIPITATION_MM));
    setInitTime(leadFrom(INIT_TIME_LEAD_MIN));
    setEndTime(leadFrom(END_TIME_LEAD_MIN));
    setError(null);
  }, [open]);

  const groupedZones = useMemo(() => {
    const groups = pumpSystems.map((ps) => ({
      name: ps.name,
      zones: zones.filter((z) => z.pumpSystemId === ps.id),
    }));
    const knownIds = new Set(pumpSystems.map((ps) => ps.id));
    const orphans = zones.filter((z) => z.pumpSystemId == null || !knownIds.has(z.pumpSystemId));
    if (orphans.length > 0) groups.push({ name: ORPHAN_GROUP, zones: orphans });
    return groups.filter((g) => g.zones.length > 0);
  }, [pumpSystems, zones]);

  const validators: Record<StartFormType, () => string | null> = {
    duration: () => (Number(durationMin) >= MIN_DURATION_MIN
      ? null : `La duración mínima es ${MIN_DURATION_MIN} minuto`),
    endTime: () => (new Date(endTime).getTime() > new Date(initTime).getTime()
      ? null : 'La hora de término debe ser posterior al inicio'),
    precipitation: () => (Number(precipitation) > 0
      ? null : 'La precipitación debe ser mayor a 0'),
  };

  const buildInput = (directSchedule: boolean): StartIrrigationInput => {
    const base: StartIrrigationInput = {
      wiseconnZoneId: Number(zoneId),
      initTime: new Date(initTime).toISOString(),
      directSchedule,
    };
    const byType: Record<StartFormType, StartIrrigationInput> = {
      duration: { ...base, durationMin: parseInt(durationMin, 10) },
      endTime: { ...base, endTime: new Date(endTime).toISOString() },
      precipitation: { ...base, precipitation: parseFloat(precipitation) },
    };
    return byType[type];
  };

  const handleSubmit = async (directSchedule: boolean) => {
    if (zoneId === '') {
      setError('Selecciona una zona');
      return;
    }
    const validationError = validators[type]();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    const ok = await onSubmit(buildInput(directSchedule));
    if (ok) onClose();
  };

  const typeField: Record<StartFormType, React.ReactNode> = {
    duration: (
      <TextField
        label="Duración [min]"
        type="number"
        value={durationMin}
        onChange={(e) => setDurationMin(e.target.value)}
        inputProps={{ min: MIN_DURATION_MIN }}
        helperText="Duración en minutos"
        required
        fullWidth
      />
    ),
    endTime: (
      <TextField
        label="Fecha y Hora Fin"
        type="datetime-local"
        value={endTime}
        onChange={(e) => setEndTime(e.target.value)}
        InputLabelProps={{ shrink: true }}
        required
        fullWidth
      />
    ),
    precipitation: (
      <TextField
        label="Volumen [mm]"
        type="number"
        value={precipitation}
        onChange={(e) => setPrecipitation(e.target.value)}
        inputProps={{ min: 0, step: 0.1 }}
        helperText="Volumen en milímetros"
        required
        fullWidth
      />
    ),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: RADIUS.dialog } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Programar riego</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Esta acción enviará comandos REALES al equipo de riego.
            </Typography>
          </Stack>

          <TextField
            select
            required
            label="Zona / Sector"
            value={zoneId === '' ? '' : String(zoneId)}
            onChange={(e) => setZoneId(Number(e.target.value))}
            fullWidth
          >
            {groupedZones.flatMap((group) => [
              <ListSubheader key={`group-${group.name}`}>{group.name}</ListSubheader>,
              ...group.zones.map((zone) => (
                <MenuItem key={zone.id} value={String(zone.id)}>{zone.name}</MenuItem>
              )),
            ])}
          </TextField>

          <TextField
            required
            label="Fecha y Hora Inicio"
            type="datetime-local"
            value={initTime}
            onChange={(e) => setInitTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <FormControl required>
            <FormLabel sx={{ mb: 0.5 }}>Tipo de Riego</FormLabel>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value as StartFormType)}
            >
              {START_TYPE_ORDER.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={START_TYPE_LABEL[option]}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {typeField[type]}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="primary" sx={pressableSx}>Cancelar</Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleSubmit(false)}
          disabled={isSending}
          sx={pressableSx}
        >
          {isSending ? 'Enviando…' : 'Enviar propuesta'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit(true)}
          disabled={isSending}
          sx={{
            bgcolor: CTA_ORANGE.main,
            color: CTA_ORANGE.contrastText,
            '&:hover': { bgcolor: CTA_ORANGE.hover },
            ...pressableSx,
          }}
        >
          {isSending ? 'Programando…' : 'Programar directo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleIrrigationModal;
